import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useScrollTrigger,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import ExtensionIcon from '@mui/icons-material/Extension';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import MemoryIcon from '@mui/icons-material/Memory';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SecurityIcon from '@mui/icons-material/Security';
import SensorsIcon from '@mui/icons-material/Sensors';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useNavigate } from 'react-router-dom';
import { getStoredAuth } from '../utils/authStorage';
import PublicLocaleToggle from '../components/PublicLocaleToggle';
import { TechBackground } from '../components/TechBackground';
import { usePublicUiLocale } from '../contexts/PublicUiLocaleContext';
import { USCADA_CONTACT_EMAIL } from '../constants/contact';
import { STORE_PLAN_PRICES } from '../project-modules/store/storeListings';
import HeroMockup from './landing/HeroMockup';
import {
  glassCardSx,
  landingTheme,
  outlinedBtnSx,
  primaryBtnSx,
} from './landing/landingTheme';

export { USCADA_CONTACT_EMAIL };

const CONTENT = {
  uk: {
    badge: 'USCADA',
    languageLabelUk: 'UA',
    languageLabelEn: 'EN',
    login: 'Увійти',
    signUp: 'Створити акаунт',
    demo: 'Запит демо',
    heroEyebrow: 'Industrial SCADA · Modular SaaS',
    heroTitle: 'Моніторинг обʼєктів з модульною платформою та особистим кабінетом',
    heroSubtitle:
      'Запускайте проєкти, керуйте UID, підключайте модулі та масштабуйте квоти — без зайвої складності на старті.',
    heroCtaStart: 'Почати безкоштовно',
    heroCtaDemo: 'Запитати демо',
    navExamples: 'Приклади',
    examplesTitle: 'Подивіться на обʼєкті',
    examplesSubtitle:
      'Без реєстрації: підніміть значення і побачте, як пакет, аварія, журнал і команда складаються в одну картину.',
    examplesOpen: 'Відкрити приклад',
    examplePlantTitle: 'Котельня',
    examplePlantText:
      'Котли, насоси і компресор. Кілька цехів і різні ролі: аварія проходить через екран оператора, журнал і тренд.',
    examplePolesTitle: 'Вуличне освітлення',
    examplePolesText:
      'Вісім опор на одному профілі. Вночі заряд падає, черговий бачить усю вулицю, підрядник — лише парк.',
    stats: [
      { value: 'Real-time', label: 'TCP телеметрія' },
      { value: '8+', label: 'Add-on модулів' },
      { value: '3', label: 'Тарифні плани' },
    ],
    stepsTitle: 'Як це працює',
    stepsSubtitle: 'Від реєстрації до робочого обʼєкта — три зрозумілі кроки',
    steps: [
      {
        icon: 'cabinet',
        step: '01',
        title: 'Особистий кабінет',
        text: 'Створюйте проєкти та UID, закріплюйте важливе зверху, дивіться тариф і квоти без входу в проєкт.',
      },
      {
        icon: 'project',
        step: '02',
        title: 'Налаштування проєкту',
        text: 'Картки, групи, теги, тривоги та користувачі — у знайомій адмін-панелі для кожного обʼєкта.',
      },
      {
        icon: 'store',
        step: '03',
        title: 'Магазин модулів',
        text: 'Тренди, Telegram, SVG, карти та інші розширення — підключайте коли потрібно, розширюйте квоти за пакетами.',
      },
    ],
    whatYouGet: 'Базові можливості платформи',
    features: [
      { icon: 'monitor', title: 'Real-time моніторинг', text: 'Живі дані з обладнання через TCP' },
      { icon: 'cards', title: 'Drilldown-картки', text: 'Групи, деталізація та SVG-мнемосхеми' },
      { icon: 'alarms', title: 'Тривоги та події', text: 'Гнучкі правила та історія подій' },
      { icon: 'uid', title: 'Профілі UID', text: 'Шаблони пристроїв для швидкого розгортання' },
      { icon: 'roles', title: 'Ролі та права', text: 'Superadmin, admin і operator рівні доступу' },
      { icon: 'modules', title: 'Модульні розширення', text: 'Докуповуйте можливості за потребою' },
    ],
    plansTitle: 'Тарифні плани',
    plansSubtitle: 'Орієнтовні ціни для комерційних проєктів. Фактичний рахунок може відрізнятися.',
    popularBadge: 'Популярний',
    plans: [
      {
        code: 'starter',
        name: 'Starter',
        description: 'Повний базовий функціонал з невеликими квотами — для пілотів і невеликих обʼєктів.',
        features: [
          'До 5 станцій',
          'До 3 користувачів',
          'Базові команди (пріоритет до 3)',
          'Тривоги, картки, code groups',
          '1 день зберігання тривог',
        ],
      },
      {
        code: 'pro',
        name: 'Pro',
        description: 'Стандартний пакет: тренди, Telegram, SVG, project tags та правила тривог.',
        features: [
          'До 50 станцій',
          'До 25 користувачів',
          'Тренди, Telegram, SVG HMI',
          'Project tags та alarm rules',
          '30 днів тривог · 90 днів трендів',
        ],
        highlighted: true,
      },
      {
        code: 'enterprise',
        name: 'Enterprise',
        description: 'Повний набір модулів: розширені команди, карти, debug-tools та високі квоти.',
        features: [
          'Безліміт станцій і користувачів',
          'Advanced commands (пріоритет 9)',
          'Карти станцій, debug tools',
          '365 днів зберігання тривог',
          'Пріоритетна підтримка',
        ],
      },
    ],
    perMonth: '/ міс',
    addonsTitle: 'Докуповуйте модулі окремо',
    addonsSubtitle:
      'На тарифі Starter можна підключити будь-який add-on. На Pro/Enterprise частина модулів уже входить у план.',
    addons: [
      { name: 'Trends', price: 29, icon: 'trends' },
      { name: 'Telegram', price: 19, icon: 'telegram' },
      { name: 'Advanced TG', price: 29, icon: 'telegram' },
      { name: 'SVG HMI', price: 49, icon: 'svg' },
      { name: 'Project tags', price: 15, icon: 'tags' },
      { name: 'Alarm rules', price: 25, icon: 'alarms' },
      { name: 'Maps', price: 22, icon: 'maps' },
    ],
    quotaNote: 'Також доступні пакети розширення квот: станції, користувачі, зберігання тривог і трендів.',
    ctaTitle: 'Готові запустити перший проєкт?',
    ctaSubtitle:
      'Зареєструйте акаунт суперадміна — і одразу потрапите в особистий кабінет з проєктами та UID.',
    ctaLogin: 'Увійти',
    ctaSignUp: 'Зареєструватися',
    footer: '© USCADA · Industrial monitoring platform',
    demoDialogTitle: 'Запит демо',
    demoDialogSubtitle: 'Напишіть нам на e-mail — ми відповімо найближчим часом.',
    demoMailButton: 'Написати лист',
    demoCopyEmail: 'Скопіювати адресу',
    demoCancel: 'Закрити',
  },
  en: {
    badge: 'USCADA',
    languageLabelUk: 'UA',
    languageLabelEn: 'EN',
    login: 'Login',
    signUp: 'Create account',
    demo: 'Request demo',
    heroEyebrow: 'Industrial SCADA · Modular SaaS',
    heroTitle: 'Industrial site monitoring on a modular SaaS platform',
    heroSubtitle:
      'Deploy projects, manage UIDs, enable modules, and scale quotas according to operational requirements.',
    heroCtaStart: 'Create account',
    heroCtaDemo: 'Request demo',
    navExamples: 'Examples',
    examplesTitle: 'See it on a real site',
    examplesSubtitle:
      'No account needed: raise a value and watch a packet, an alarm, the journal and a command come together.',
    examplesOpen: 'Open the example',
    examplePlantTitle: 'Boiler house',
    examplePlantText:
      'Boilers, pumps and a compressor. Several shops and different roles: a fault shows up on the operator screen, in the journal and on the trend.',
    examplePolesTitle: 'Street lighting',
    examplePolesText:
      'Eight poles on one profile. At night the charge falls, the dispatcher sees the whole street, the contractor only the park.',
    stats: [
      { value: 'Real-time', label: 'TCP telemetry' },
      { value: '8+', label: 'Add-on modules' },
      { value: '3', label: 'Pricing tiers' },
    ],
    stepsTitle: 'How it works',
    stepsSubtitle: 'From account registration to a live deployment in three steps',
    steps: [
      {
        icon: 'cabinet',
        step: '01',
        title: 'Customer portal',
        text: 'Create projects and UIDs, manage subscriptions, and review plan limits from a central dashboard.',
      },
      {
        icon: 'project',
        step: '02',
        title: 'Project setup',
        text: 'Configure cards, groups, tags, alarms, and users in the project administration panel.',
      },
      {
        icon: 'store',
        step: '03',
        title: 'Module catalog',
        text: 'Activate trends, Telegram, SVG HMI, maps, and other modules; extend quotas with add-on packages.',
      },
    ],
    whatYouGet: 'Core platform capabilities',
    features: [
      { icon: 'monitor', title: 'Real-time monitoring', text: 'Live equipment data over TCP' },
      { icon: 'cards', title: 'Drilldown cards', text: 'Groups, detail views and SVG mimics' },
      { icon: 'alarms', title: 'Alarms & events', text: 'Flexible rules and event history' },
      { icon: 'uid', title: 'UID profiles', text: 'Device templates for fast rollout' },
      { icon: 'roles', title: 'Roles & permissions', text: 'Superadmin, admin and operator tiers' },
      { icon: 'modules', title: 'Modular extensions', text: 'Activate additional capabilities per project requirements' },
    ],
    plansTitle: 'Pricing plans',
    plansSubtitle: 'Indicative pricing for commercial projects. Final billing may differ.',
    popularBadge: 'Popular',
    plans: [
      {
        code: 'starter',
        name: 'Starter',
        description: 'Full core feature set with low quotas — for pilots and small sites.',
        features: [
          'Up to 5 stations',
          'Up to 3 users',
          'Basic commands (priority up to 3)',
          'Alarms, cards, code groups',
          '1 day alarm retention',
        ],
      },
      {
        code: 'pro',
        name: 'Pro',
        description: 'Standard package: trends, Telegram, SVG, project tags and alarm rules.',
        features: [
          'Up to 50 stations',
          'Up to 25 users',
          'Trends, Telegram, SVG HMI',
          'Project tags and alarm rules',
          '30 days alarms · 90 days trends',
        ],
        highlighted: true,
      },
      {
        code: 'enterprise',
        name: 'Enterprise',
        description: 'Full module set: advanced commands, maps, debug tools and high quotas.',
        features: [
          'Unlimited stations and users',
          'Advanced commands (priority 9)',
          'Station maps, debug tools',
          '365 days alarm retention',
          'Priority support',
        ],
      },
    ],
    perMonth: '/ mo',
    addonsTitle: 'Optional add-on modules',
    addonsSubtitle:
      'Individual modules can be activated on the Starter plan. Pro and Enterprise include an extended module set.',
    addons: [
      { name: 'Trends', price: 29, icon: 'trends' },
      { name: 'Telegram', price: 19, icon: 'telegram' },
      { name: 'Advanced TG', price: 29, icon: 'telegram' },
      { name: 'SVG HMI', price: 49, icon: 'svg' },
      { name: 'Project tags', price: 15, icon: 'tags' },
      { name: 'Alarm rules', price: 25, icon: 'alarms' },
      { name: 'Maps', price: 22, icon: 'maps' },
    ],
    quotaNote: 'Quota extension packages are also available: stations, users, alarm and trend retention.',
    ctaTitle: 'Deploy your first monitoring project',
    ctaSubtitle:
      'Register a superadmin account to access the customer portal with project and UID management.',
    ctaLogin: 'Login',
    ctaSignUp: 'Sign up',
    footer: '© USCADA · Industrial monitoring platform',
    demoDialogTitle: 'Request demo',
    demoDialogSubtitle: 'Contact us by email. Our team will respond promptly.',
    demoMailButton: 'Send e-mail',
    demoCopyEmail: 'Copy address',
    demoCancel: 'Close',
  },
};

function StepIcon({ type }) {
  const sx = { fontSize: 26, color: landingTheme.accentBright };
  if (type === 'cabinet') return <FolderOpenIcon sx={sx} />;
  if (type === 'store') return <StorefrontIcon sx={sx} />;
  return <MemoryIcon sx={sx} />;
}

StepIcon.propTypes = {
  type: PropTypes.oneOf(['cabinet', 'project', 'store']).isRequired,
};

function FeatureIcon({ type }) {
  const sx = { fontSize: 28, color: landingTheme.cyan };
  if (type === 'monitor') return <SensorsIcon sx={sx} />;
  if (type === 'cards') return <DashboardCustomizeIcon sx={sx} />;
  if (type === 'alarms') return <NotificationsActiveIcon sx={sx} />;
  if (type === 'uid') return <MemoryIcon sx={sx} />;
  if (type === 'roles') return <SecurityIcon sx={sx} />;
  return <ExtensionIcon sx={sx} />;
}

FeatureIcon.propTypes = {
  type: PropTypes.oneOf(['monitor', 'cards', 'alarms', 'uid', 'roles', 'modules']).isRequired,
};

function formatPlanPrice(code) {
  const monthly = STORE_PLAN_PRICES[code]?.monthly;
  if (monthly == null || monthly <= 0) return null;
  return `$${monthly}`;
}

function LandingNav({ t, lang, setLang, scrolled, onExamples, onDemo, onLogin, onSignUp }) {
  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        py: 1.5,
        mb: { xs: 2, md: 4 },
        transition: 'background 0.3s, backdrop-filter 0.3s, border-color 0.3s',
        ...(scrolled
          ? {
              bgcolor: 'rgba(5, 10, 20, 0.82)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderBottom: `1px solid ${landingTheme.glassBorder}`,
            }
          : { bgcolor: 'transparent' }),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${landingTheme.accent}, ${landingTheme.cyan})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: 0.5,
              boxShadow: '0 4px 16px rgba(92,108,255,0.35)',
            }}
          >
            U
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 18, letterSpacing: 0.5, color: landingTheme.text }}>
            {t.badge}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <PublicLocaleToggle
            lang={lang}
            onChange={setLang}
            labelUk={t.languageLabelUk}
            labelEn={t.languageLabelEn}
          />
          <Button
            variant="text"
            size="small"
            onClick={onExamples}
            sx={{ color: landingTheme.textSecondary, textTransform: 'none', fontWeight: 700, display: { xs: 'none', md: 'inline-flex' } }}
          >
            {t.navExamples}
          </Button>
          <Button variant="outlined" size="small" onClick={onDemo} sx={outlinedBtnSx}>
            {t.demo}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onSignUp}
            sx={{ ...outlinedBtnSx, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {t.signUp}
          </Button>
          <Button variant="contained" size="small" onClick={onLogin} sx={primaryBtnSx}>
            {t.login}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

LandingNav.propTypes = {
  t: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  setLang: PropTypes.func.isRequired,
  scrolled: PropTypes.bool.isRequired,
  onExamples: PropTypes.func.isRequired,
  onDemo: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  onSignUp: PropTypes.func.isRequired,
};

function SectionHeading({ title, subtitle }) {
  return (
    <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: landingTheme.text,
          fontSize: { xs: '1.6rem', md: '2.1rem' },
          mb: subtitle ? 1 : 0,
        }}
      >
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body1" sx={{ color: landingTheme.textSecondary, maxWidth: 560, mx: 'auto' }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

SectionHeading.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

SectionHeading.defaultProps = {
  subtitle: '',
};

export default function LandingPage() {
  const navigate = useNavigate();
  const isPhone = useMediaQuery('(max-width:600px)');
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 24 });
  const { lang, setLang } = usePublicUiLocale();
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    if (!isPhone) return;
    const storedAuth = getStoredAuth();
    const user = storedAuth?.user || {};
    if (!storedAuth?.token || !user?.result) return;
    const role = String(user.role || '').toLowerCase();
    const projectSlug = String(
      user.projectSlug || localStorage.getItem('lastProjectSlug') || '',
    ).trim();
    if (role === 'superadmin') {
      navigate('/v2/superadmin', { replace: true });
      return;
    }
    if (role === 'support') {
      navigate('/v2/support', { replace: true });
      return;
    }
    if (projectSlug && (role === 'admin' || role === 'user')) {
      const nextPath =
        role === 'admin'
          ? `/p/${projectSlug}/admin`
          : `/p/${projectSlug}/groups`;
      navigate(nextPath, { replace: true });
      return;
    }
    navigate('/v2/login', { replace: true });
  }, [isPhone, navigate]);

  useEffect(() => {
    if (window.location.hash !== '#examples') return undefined;
    const timer = setTimeout(() => {
      document.getElementById('examples')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const host = window.location.hostname.replace(/^www\./, '');
    if (host !== 'localhost' && host !== '127.0.0.1' && host !== 'uscada.pro') return undefined;
    const prevTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const prevDescription = description?.getAttribute('content') || '';
    document.title = lang === 'en'
      ? 'Cloud SCADA USCADA — site monitoring and dispatch'
      : 'Хмарна SCADA USCADA — моніторинг і диспетчеризація обʼєктів';
    if (description) {
      description.setAttribute(
        'content',
        lang === 'en'
          ? 'USCADA is cloud SCADA: live equipment data, alarms, an event journal, trends and mimic diagrams in the browser.'
          : 'USCADA приймає дані з обладнання, показує стан обʼєкта, фіксує аварії і зберігає історію. Доступ у браузері за тарифом.',
      );
    }
    return () => {
      document.title = prevTitle;
      if (description) description.setAttribute('content', prevDescription);
    };
  }, [lang]);

  const t = useMemo(() => CONTENT[lang] || CONTENT.en, [lang]);
  const host = window.location.hostname.replace(/^www\./, '');
  const showBrochure = host === 'localhost' || host === '127.0.0.1' || host === 'uscada.pro';
  const brochureLinkSx = {
    color: landingTheme.cyan,
    fontWeight: 700,
    textDecoration: 'none',
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        bgcolor: landingTheme.bg,
        color: landingTheme.text,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse 80% 50% at 20% -10%, rgba(92,108,255,0.22), transparent),
            radial-gradient(ellipse 60% 40% at 90% 10%, rgba(0,188,212,0.14), transparent),
            radial-gradient(ellipse 50% 30% at 50% 100%, rgba(157,123,255,0.12), transparent)
          `,
          pointerEvents: 'none',
        }}
      />
      <TechBackground />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pb: 6 }}>
        <LandingNav
          t={t}
          lang={lang}
          setLang={setLang}
          scrolled={scrolled}
          onExamples={() => document.getElementById('examples')?.scrollIntoView({ behavior: 'smooth' })}
          onDemo={() => setDemoOpen(true)}
          onLogin={() => navigate('/v2/login')}
          onSignUp={() => navigate('/v2/login?mode=register')}
        />

        {/* Hero */}
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" sx={{ mb: { xs: 6, md: 10 } }}>
          <Grid item xs={12} md={6}>
            <Chip
              label={t.heroEyebrow}
              size="small"
              sx={{
                mb: 2,
                color: landingTheme.cyan,
                bgcolor: 'rgba(0,188,212,0.1)',
                border: '1px solid rgba(0,188,212,0.25)',
                fontWeight: 600,
                letterSpacing: 0.3,
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.08,
                fontSize: { xs: '2rem', sm: '2.6rem', md: '3.2rem' },
                mb: 2,
                background: landingTheme.gradientHero,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {t.heroTitle}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: landingTheme.textSecondary,
                fontWeight: 400,
                lineHeight: 1.65,
                mb: 3,
                fontSize: { xs: '1rem', md: '1.15rem' },
              }}
            >
              {t.heroSubtitle}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 4 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/v2/login?mode=register')}
                sx={{ ...primaryBtnSx, px: 3 }}
              >
                {t.heroCtaStart}
              </Button>
              <Button
                variant="outlined"
                size="large"
                data-help="landing-demo"
                onClick={() => setDemoOpen(true)}
                sx={{ ...outlinedBtnSx, px: 3 }}
              >
                {t.heroCtaDemo}
              </Button>
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              {t.stats.map((stat) => (
                <Box key={stat.label}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: landingTheme.text }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: landingTheme.textMuted }}>
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={6} data-help="landing-pic">
            <HeroMockup />
          </Grid>
        </Grid>

        <Box id="examples" data-help="landing-examples" sx={{ mb: { xs: 6, md: 10 }, scrollMarginTop: 88 }}>
          <SectionHeading title={t.examplesTitle} subtitle={t.examplesSubtitle} />
          <Grid container spacing={2.5}>
            {[
              {
                kind: 'plant',
                title: t.examplePlantTitle,
                text: t.examplePlantText,
                icon: <FactoryOutlinedIcon sx={{ color: landingTheme.cyan }} />,
              },
              {
                kind: 'poles',
                title: t.examplePolesTitle,
                text: t.examplePolesText,
                icon: <LightbulbOutlinedIcon sx={{ color: landingTheme.cyan }} />,
              },
            ].map((card) => (
              <Grid item xs={12} md={6} key={card.kind}>
                <Box
                  component="button"
                  type="button"
                  sx={{
                    ...glassCardSx,
                    p: 3,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    textAlign: 'left',
                    color: 'inherit',
                    font: 'inherit',
                    cursor: 'pointer',
                    transition: 'transform 0.25s, border-color 0.25s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: 'rgba(0, 188, 212, 0.4)',
                    },
                  }}
                  onClick={() => navigate(`/examples/${card.kind}`)}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      mb: 2,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0, 188, 212, 0.1)',
                      border: '1px solid rgba(0, 188, 212, 0.25)',
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: landingTheme.text }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: landingTheme.textSecondary, lineHeight: 1.7, mb: 2.5, flex: 1 }}>
                    {card.text}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: landingTheme.cyan, fontWeight: 700 }}>
                    <Typography sx={{ fontWeight: 700, color: landingTheme.cyan }}>{t.examplesOpen}</Typography>
                    <ArrowForwardIcon sx={{ fontSize: 18 }} />
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Steps */}
        <Box data-help="landing-steps" sx={{ mb: { xs: 6, md: 10 } }}>
          <SectionHeading title={t.stepsTitle} subtitle={t.stepsSubtitle} />
          <Grid container spacing={2.5}>
            {t.steps.map((step) => (
              <Grid item xs={12} md={4} key={step.title}>
                <Box
                  sx={{
                    ...glassCardSx,
                    p: 3,
                    height: '100%',
                    transition: 'transform 0.25s, border-color 0.25s, box-shadow 0.25s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: 'rgba(92, 108, 255, 0.35)',
                      boxShadow: `${landingTheme.shadowCard}, ${landingTheme.shadowGlow}`,
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(92, 108, 255, 0.12)',
                        border: '1px solid rgba(92, 108, 255, 0.2)',
                      }}
                    >
                      <StepIcon type={step.icon} />
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '2rem',
                        lineHeight: 1,
                        color: 'rgba(255,255,255,0.06)',
                        fontFamily: 'monospace',
                      }}
                    >
                      {step.step}
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: landingTheme.text }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: landingTheme.textSecondary, lineHeight: 1.7 }}>
                    {step.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features */}
        <Box sx={{ mb: { xs: 6, md: 10 } }}>
          <SectionHeading title={t.whatYouGet} />
          <Grid container spacing={2}>
            {t.features.map((feat) => (
              <Grid item xs={12} sm={6} md={4} key={feat.title}>
                <Box
                  sx={{
                    ...glassCardSx,
                    p: 2.5,
                    height: '100%',
                    borderRadius: 2.5,
                  }}
                >
                  <FeatureIcon type={feat.icon} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, color: landingTheme.text }}>
                    {feat.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: landingTheme.textSecondary, lineHeight: 1.6 }}>
                    {feat.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Pricing */}
        <Box sx={{ mb: { xs: 6, md: 10 } }}>
          <SectionHeading title={t.plansTitle} subtitle={t.plansSubtitle} />
          <Grid container spacing={2.5} alignItems="stretch">
            {t.plans.map((plan) => {
              const price = formatPlanPrice(plan.code);
              const highlighted = plan.highlighted;
              return (
                <Grid item xs={12} md={4} key={plan.code}>
                  <Box
                    sx={{
                      ...glassCardSx,
                      p: 3,
                      height: '100%',
                      position: 'relative',
                      border: highlighted
                        ? '1px solid rgba(92, 108, 255, 0.45)'
                        : glassCardSx.border,
                      background: highlighted
                        ? 'linear-gradient(180deg, rgba(92,108,255,0.14) 0%, rgba(12,20,38,0.85) 100%)'
                        : glassCardSx.background,
                      boxShadow: highlighted
                        ? `${landingTheme.shadowCard}, 0 0 60px rgba(92,108,255,0.15)`
                        : landingTheme.shadowCard,
                      transform: highlighted ? { md: 'scale(1.03)' } : 'none',
                    }}
                  >
                    {highlighted ? (
                      <Chip
                        label={t.popularBadge}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          bgcolor: landingTheme.accent,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: 11,
                        }}
                      />
                    ) : null}
                    <Typography variant="overline" sx={{ color: landingTheme.textMuted, letterSpacing: 1.5 }}>
                      {plan.name}
                    </Typography>
                    {price ? (
                      <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ my: 1.5 }}>
                        <Typography variant="h3" sx={{ fontWeight: 800, color: landingTheme.text }}>
                          {price}
                        </Typography>
                        <Typography variant="body2" sx={{ color: landingTheme.textMuted }}>
                          {t.perMonth}
                        </Typography>
                      </Stack>
                    ) : null}
                    <Typography variant="body2" sx={{ color: landingTheme.textSecondary, mb: 2.5, minHeight: 44 }}>
                      {plan.description}
                    </Typography>
                    <Stack spacing={1.25}>
                      {plan.features.map((f) => (
                        <Stack key={f} direction="row" spacing={1} alignItems="flex-start">
                          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: landingTheme.cyan, mt: 0.15, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: landingTheme.text, lineHeight: 1.5 }}>
                            {f}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Add-ons */}
        <Box sx={{ mb: { xs: 6, md: 10 } }}>
          <SectionHeading title={t.addonsTitle} subtitle={t.addonsSubtitle} />
          <Grid container spacing={1.5}>
            {t.addons.map((addon) => (
              <Grid item xs={6} sm={4} md={2} key={addon.name}>
                <Box
                  sx={{
                    ...glassCardSx,
                    p: 2,
                    textAlign: 'center',
                    borderRadius: 2.5,
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: 'rgba(0,188,212,0.35)' },
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 22, color: landingTheme.violet, mb: 0.75 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: landingTheme.text, mb: 0.25 }}>
                    {addon.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: landingTheme.cyan, fontWeight: 700 }}>
                    ${addon.price}{t.perMonth}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Typography
            variant="caption"
            sx={{ color: landingTheme.textMuted, display: 'block', textAlign: 'center', mt: 2.5 }}
          >
            {t.quotaNote}
          </Typography>
        </Box>

        {/* CTA */}
        <Box
          sx={{
            ...glassCardSx,
            p: { xs: 3, md: 5 },
            textAlign: 'center',
            background: landingTheme.gradientCta,
            border: '1px solid rgba(92, 108, 255, 0.25)',
            boxShadow: `${landingTheme.shadowCard}, ${landingTheme.shadowGlow}`,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5, color: landingTheme.text }}>
            {t.ctaTitle}
          </Typography>
          <Typography variant="body1" sx={{ color: landingTheme.textSecondary, mb: 3, maxWidth: 520, mx: 'auto' }}>
            {t.ctaSubtitle}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/v2/login?mode=register')}
              sx={{ ...primaryBtnSx, px: 4 }}
            >
              {t.ctaSignUp}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/v2/login')}
              sx={{ ...outlinedBtnSx, px: 4 }}
            >
              {t.ctaLogin}
            </Button>
          </Stack>
        </Box>

        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', mt: 4, color: landingTheme.textMuted }}
        >
          {t.footer}
        </Typography>
        {showBrochure ? (
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            {[
              ['/services/', lang === 'en' ? 'Services' : 'Послуги'],
              ['/scada/', 'SCADA'],
              ['/monitoring/', lang === 'en' ? 'Monitoring' : 'Моніторинг'],
              ['/alarms/', lang === 'en' ? 'Alarms' : 'Аварії'],
              ['/hmi/', lang === 'en' ? 'Mimics' : 'Мнемосхеми'],
              ['/pricing/', lang === 'en' ? 'Pricing' : 'Тарифи'],
              ['/about/', lang === 'en' ? 'Provider' : 'Виконавець'],
              ['/contacts/', lang === 'en' ? 'Contacts' : 'Контакти'],
            ].map(([href, label]) => {
              const localizedHref = lang === 'en' ? `/en${href}` : href;
              return (
              <Typography key={localizedHref} component="a" href={localizedHref} variant="caption" sx={brochureLinkSx}>
                {label}
              </Typography>
            );
            })}
          </Stack>
        ) : null}
      </Container>

      <Dialog
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: landingTheme.bgAlt,
            color: landingTheme.text,
            border: `1px solid ${landingTheme.glassBorder}`,
          },
        }}
      >
        <DialogTitle>{t.demoDialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, color: landingTheme.textSecondary }}>
            {t.demoDialogSubtitle}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>
            {USCADA_CONTACT_EMAIL}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button onClick={() => setDemoOpen(false)} sx={{ color: landingTheme.textSecondary }}>
            {t.demoCancel}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              navigator.clipboard?.writeText(USCADA_CONTACT_EMAIL).catch(() => {});
            }}
            sx={outlinedBtnSx}
          >
            {t.demoCopyEmail}
          </Button>
          <Button
            component="a"
            href={`mailto:${USCADA_CONTACT_EMAIL}?subject=${encodeURIComponent('USCADA demo request')}`}
            variant="contained"
            onClick={() => setDemoOpen(false)}
            sx={primaryBtnSx}
          >
            {t.demoMailButton}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
